import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, ModalContent, Button, Input } from "@nextui-org/react"; 
import { ExclamationCircleIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select'; 
import { BASE_URL, getFetcher } from "../../../../services/api";
import toast from 'react-hot-toast'; 
import { bacaJawabanUnggahan } from '../../../../services/errorMessage';

const initialFormData = {
  type_registrasi: '', status_izin: 'Seumur Hidup', tanggal_terbit: '', berlaku_sampai: '9999-12-31',
  nomor_surat_formatted: '', no_reg_bahan: '', 
  bahan_b3: [
    { cas_number: '', nama_bahan: '', nama_dagang: '', hs_code: '', tujuan_penggunaan: '' }
  ],
  asal_negara: '', pelabuhan_bongkar: [], file_sk: null
};

export default function ModalSKManual({ isOpen, onClose, selectedCompany, onSuccess }) {
  // --- States ---
  const [companyInfo, setCompanyInfo] = useState({ name: '-', penanggungJawab: '-', idCompanySimpel: '-' });
  const [masterBahan, setMasterBahan] = useState([]);
  const [listNegara, setListNegara] = useState([]);
  const [listPelabuhanBongkar, setListPelabuhanBongkar] = useState([]);

  const [, setLoadingCompany] = useState(false);
  const [loadingBahan, setLoadingBahan] = useState(false);
  
  const [loadingPelabuhanBongkar, setLoadingPelabuhanBongkar] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState(initialFormData);

  // --- DRAGGABLE LOGIC ---
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => { 
    if (!isOpen) { 
      setPosition({ x: 0, y: 0 });
      setFormData(initialFormData);
    }
  }, [isOpen]);

  const handlePointerDown = (e) => {
    setIsDragging(true); dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y }; e.target.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e) => { if (isDragging) setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }); };
  const handlePointerUp = (e) => { setIsDragging(false); e.target.releasePointerCapture(e.pointerId); };

  // --- API FETCHERS ---
  useEffect(() => {
    if (isOpen && selectedCompany?.value) {
      setLoadingCompany(true);
      getFetcher(`/api/company/search-company?companyIds=${selectedCompany.value}&returnAll=true`)
        .then(data => {
          const target = data?.data?.[0] || data?.[0] || {};
          setCompanyInfo({ 
            id: target?.id || selectedCompany.value, 
            name: target?.name || selectedCompany.label || '-', 
            penanggungJawab: target?.penanggungJawab || '-', 
            idCompanySimpel: target?.idCompanySimpel || '-' 
          });
        }).finally(() => setLoadingCompany(false));
    }
  }, [isOpen, selectedCompany]);

  useEffect(() => {
    if (isOpen) {
      setLoadingBahan(true);
      getFetcher('/api/data-master/bahan-b3?returnAll=true').then(data => setMasterBahan(data?.data || data || [])).finally(() => setLoadingBahan(false));
      getFetcher('/api/countries').then(data => setListNegara(data?.data || data || []));
      setLoadingPelabuhanBongkar(true);
      getFetcher('/api/data-pelabuhan/by-negara?asal_negara=ID').then(data => setListPelabuhanBongkar(data?.data || data || [])).finally(() => setLoadingPelabuhanBongkar(false));
    }
  }, [isOpen]);

  // --- MEMOIZE UNTUK MENCEGAH INFINITE LOOP REACT-SELECT ---
  const pelabuhanOptions = useMemo(() => {
    return listPelabuhanBongkar.map(p => ({ 
      value: p.id_lokasi, 
      label: `${p.id_lokasi} - ${p.nm_lokasi}` 
    }));
  }, [listPelabuhanBongkar]);

  const pelabuhanValue = useMemo(() => {
    return pelabuhanOptions.filter(p => formData.pelabuhan_bongkar.includes(p.value));
  }, [pelabuhanOptions, formData.pelabuhan_bongkar]);

  const bahanOptions = useMemo(() => {
    return masterBahan.map(b => ({
      value: b.casNumber,
      label: `${b.casNumber} - ${b.namaBahanKimia}`
    }));
  }, [masterBahan]);

  const negaraOptions = useMemo(() => {
    return listNegara.map(n => ({
      value: n.code2,
      label: n.name
    }));
  }, [listNegara]);

  // --- FORM LOGIC ---
  const kodeRegistrasi = useMemo(() => {
    if (!formData.type_registrasi) return '';
    // Awalan kode: I = impor, P = produsen, L = lainnya.
    const prefix = { import: 'I', produsen: 'P', lainnya: 'L' }[formData.type_registrasi] ?? 'I';
    const now = new Date();
    return `${prefix}${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getTime()).slice(-4)}`;
  }, [formData.type_registrasi]);

  // --- LOGIC MULTIPLE B3 ---
  const handleAddB3 = () => {
    setFormData(prev => ({
      ...prev,
      bahan_b3: [...prev.bahan_b3, { cas_number: '', nama_bahan: '', nama_dagang: '', hs_code: '', tujuan_penggunaan: '' }]
    }));
  };

  const handleRemoveB3 = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      bahan_b3: prev.bahan_b3.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleB3Change = (index, field, value) => {
    setFormData(prev => {
      const newBahanB3 = [...prev.bahan_b3];
      if (field === 'cas_number') {
        const bahanObj = masterBahan.find(b => b.casNumber === value);
        newBahanB3[index] = {
           ...newBahanB3[index],
           cas_number: value,
           nama_bahan: bahanObj?.namaBahanKimia || '',
           nama_dagang: bahanObj?.namaDagang || '',
           kategoriBahanId: bahanObj?.kategoriBahanId || null,
          };
      } else if (field === 'hs_code') {
        newBahanB3[index][field] = value.replace(/\D/g, ''); 
      } else {
        newBahanB3[index][field] = value;
      }
      return { ...prev, bahan_b3: newBahanB3 };
    });
  };

  // --- VALIDATION & SUBMIT ---
  const handlePreSubmit = (e) => {
    e.preventDefault();
    const patterns = {
      nomor_surat: { 
        regex: /^[A-Z]\.?\d{1,5}\/.+\/(0[1-9]|1[0-2]|I|II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)\/\d{4}$/, 
        msg: "Format Nomor SK tidak valid. Contoh: S.9999/PB3/PPI/PLB.4.4/B/04/2024" 
      },
      no_reg: { 
        regex: /^(\d{4,6}|\d{4}\.\d{1,4})\.\d{1,7}-\d{2}-\d\.\d{2,3}\.\d{3}$/, 
        msg: "Format Nomor Registrasi B3 tidak valid. Contoh: 2026.02.108-78-1.86.029" 
      },
      hs_code: { 
        regex: /^[\d.]+$/, 
        msg: "HS Code hanya boleh berisi angka dan titik." 
      }
    };

    // Validasi untuk React Select yang tidak mempan oleh property `required` bawaan HTML5
    if (!formData.asal_negara) return toast.error("Mohon pilih Negara Asal");

    for (let i = 0; i < formData.bahan_b3.length; i++) {
      const b3 = formData.bahan_b3[i];
      if (!b3.cas_number || !b3.hs_code || !b3.tujuan_penggunaan || !b3.nama_dagang) {
        return toast.error(`Mohon lengkapi seluruh field B3 pada baris ke-${i + 1}`);
      }
      if (!patterns.hs_code.regex.test(b3.hs_code)) {
        return toast.error(`HS Code pada baris ke-${i + 1} tidak valid. ${patterns.hs_code.msg}`);
      }
    }
    
    // Validasi Pelabuhan hanya jika BUKAN produsen
    if (formData.type_registrasi !== 'produsen' && formData.pelabuhan_bongkar.length === 0) {
      return toast.error(`Mohon pilih minimal 1 opsi pada field PELABUHAN BONGKAR`);
    }

    if (!formData.file_sk) return toast.error("Mohon unggah file SK Anda.");

    setShowConfirm(true);
  };

  const handleExecuteSave = async () => {
    setShowConfirm(false); setSubmitting(true);
    const payload = new FormData();
    Object.keys(formData).forEach(k => payload.append(k, Array.isArray(formData[k]) ? JSON.stringify(formData[k]) : formData[k]));
    payload.append('companyId', companyInfo.id);
    payload.append('idCompanySimpel', companyInfo.idCompanySimpel);
    payload.append('nomor', kodeRegistrasi);
    
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    
    try {
      const res = await fetch(`${BASE_URL}/api/sk-manual/registrasi`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: payload });
      const result = await bacaJawabanUnggahan(res);
      if (res.ok) { 
        toast.success("BERHASIL! SK Manual Berhasil disimpan."); 
        setFormData(initialFormData); 
        onSuccess(); 
        onClose(); 
      } else {
        toast.error(`GAGAL MENYIMPAN! Keterangan: ${result.message || 'Error server.'}`);
      }
    } catch (e) { 
      toast.error(`Terjadi kesalahan: ${e.message}`); 
    } finally { 
      setSubmitting(false); 
    }
  };

  // --- STYLING ---
  const inputClass = "w-full border border-line-strong rounded-lg px-2.5 py-1.5 h-8 text-xs bg-white focus:ring-2 focus:ring-blue-500/50";
  const labelClass = "text-[11px] font-semibold text-ink-700 mb-1 block";
  const sectionClass = "bg-surface-2/50 border border-line rounded-xl p-3.5 mb-1.5 space-y-3";
  const reqMark = <span className="text-red-500 ml-0.5">*</span>; // Indikator field wajib

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: '32px', fontSize: '12px', borderRadius: '0.5rem', borderColor: '#C7D2CF', boxShadow: 'none', '&:hover': { borderColor: '#147DEF' } }),
    menu: (base) => ({ ...base, fontSize: '12px', zIndex: 9999 }), 
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }), 
    input: (base) => ({ ...base, margin: 0, padding: 0 }), 
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }), 
    clearIndicator: (base) => ({ ...base, padding: '4px' })
  };

  return (
    <>
      <Modal isDismissable={false} isOpen={isOpen && !showConfirm} onClose={onClose} size="full" hideCloseButton className="bg-transparent shadow-none" disableAnimation>
        <ModalContent className="flex items-center justify-center pointer-events-none">
          <div style={{ transform: `translate(${position.x}px, ${position.y}px)` }} className="pointer-events-auto bg-white rounded-2xl shadow-2xl flex flex-col font-sans max-w-[75vw] w-full h-auto max-h-[92vh] border border-line">
            <form onSubmit={handlePreSubmit} className="flex flex-col h-full overflow-hidden">
              
              {/* DRAGGABLE HEADER */}
              <div onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} className="px-4 py-3 bg-gradient-to-r from-g-800 to-g-700 flex justify-between items-center cursor-move rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5 opacity-60">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div><div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-white font-bold text-sm ml-2">Form Penambahan SK Manual</span>
                </div>
                <button type="button" onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full"><XMarkIcon className="size-5" /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                
                {/* 1. IDENTITAS */}
                <div className="grid grid-cols-4 gap-3 bg-surface-3 p-3 rounded-xl border">
                  <div><label className={labelClass}>Name</label><Input isReadOnly size="sm" variant="flat" value={companyInfo.name} /></div>
                  <div><label className={labelClass}>Penanggungjawab</label><Input isReadOnly size="sm" variant="flat" value={companyInfo.penanggungJawab} /></div>
                  <div><label className={labelClass}>idCompanySimpel</label><Input isReadOnly size="sm" variant="flat" value={companyInfo.idCompanySimpel} /></div>
                  <div><label className={labelClass}>Kode Registrasi</label><Input isReadOnly size="sm" variant="flat" className="font-mono text-blue-700 font-bold" value={kodeRegistrasi} /></div>
                </div>

                {/* 2. DETAIL SK */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-[13px] border-b pb-1.5 flex items-center gap-2"><span className="w-1.5 h-4 bg-blue-600 rounded-full"></span> Detail Surat Permohonan SK :</h3>
                  <div className="flex gap-3">
                    <div className="w-[15%]">
                      <label className={labelClass}>Jenis Permohonan {reqMark}</label>
                      <select 
                        required
                        className={inputClass} 
                        value={formData.type_registrasi} 
                        onChange={e => {
                          const val = e.target.value;
                          setFormData(prev => {
                            const updates = { type_registrasi: val };
                            // ---> Logika jika Produsen <---
                            if (val === 'produsen') {
                              updates.asal_negara = 'ID'; 
                              updates.pelabuhan_bongkar = []; 
                            } else if (prev.type_registrasi === 'produsen' && val !== 'produsen') {
                              updates.asal_negara = ''; 
                            }
                            return { ...prev, ...updates };
                          });
                        }}
                      >
                        <option value="">-- Pilih --</option>
                        <option value="import">Impor B3</option>
                        <option value="produsen">Produsen B3</option>
                        {/* "Lainnya" memakai isian yang sama persis dengan Impor B3;
                            seluruh percabangan di berkas ini menguji 'produsen', jadi
                            nilai lain otomatis mengikuti alur impor. */}
                        <option value="lainnya">Lainnya</option>
                      </select>
                    </div>
                    <div className="w-[15%]">
                      <label className={labelClass}>Status Izin {reqMark}</label>
                      <select 
                        required
                        className={inputClass} 
                        value={formData.status_izin} 
                        onChange={e => {
                          const newStatus = e.target.value;
                          setFormData(prev => {
                            let newBerlakuSampai = prev.berlaku_sampai;
                            if (newStatus === 'Seumur Hidup') {
                              newBerlakuSampai = '9999-12-31';
                            } else if (newStatus === 'Berjangka' && prev.tanggal_terbit) {
                              const d = new Date(prev.tanggal_terbit);
                              d.setFullYear(d.getFullYear() + 1);
                              const year = d.getFullYear();
                              const month = String(d.getMonth() + 1).padStart(2, '0');
                              const day = String(d.getDate()).padStart(2, '0');
                              newBerlakuSampai = `${year}-${month}-${day}`;
                            } else if (newStatus === 'Berjangka' && !prev.tanggal_terbit) {
                              newBerlakuSampai = ''; 
                            }
                            return { ...prev, status_izin: newStatus, berlaku_sampai: newBerlakuSampai };
                          });
                        }}
                      >
                        <option value="Seumur Hidup">Seumur Hidup</option>
                        <option value="Berjangka">Berjangka</option>
                      </select>
                    </div>

                    <div className="w-[14%]">
                      <label className={labelClass}>Tanggal Terbit {reqMark}</label>
                      <input 
                        required
                        type="date" 
                        className={inputClass} 
                        value={formData.tanggal_terbit}
                        onChange={e => {
                          const selectedDate = e.target.value;
                          setFormData(prev => {
                            let newBerlakuSampai = prev.berlaku_sampai;
                            if (prev.status_izin === 'Seumur Hidup') {
                              newBerlakuSampai = '9999-12-31';
                            } else if (prev.status_izin === 'Berjangka' && selectedDate) {
                              const d = new Date(selectedDate);
                              d.setFullYear(d.getFullYear() + 1);
                              const year = d.getFullYear();
                              const month = String(d.getMonth() + 1).padStart(2, '0');
                              const day = String(d.getDate()).padStart(2, '0');
                              newBerlakuSampai = `${year}-${month}-${day}`;
                            }
                            return { ...prev, tanggal_terbit: selectedDate, berlaku_sampai: newBerlakuSampai };
                          });
                        }} 
                      />
                    </div>
                    <div className="w-[14%]">
                      <label className={labelClass}>Berlaku Sampai {reqMark}</label>
                      <input 
                        required
                        type="date" 
                        disabled={formData.status_izin === 'Seumur Hidup'} 
                        className={`${inputClass} ${formData.status_izin === 'Seumur Hidup' ? 'bg-surface-3 text-ink-400 cursor-not-allowed' : ''}`} 
                        value={formData.berlaku_sampai} 
                        onChange={e => setFormData(p => ({ ...p, berlaku_sampai: e.target.value }))} 
                      />
                    </div>
                    <div className="w-[22%]">
                      <label className={labelClass}>Nomor SK {reqMark}</label>
                      <input required className={inputClass} placeholder="Contoh: S.9999/PB3..." value={formData.nomor_surat_formatted} onChange={e => setFormData(p => ({ ...p, nomor_surat_formatted: e.target.value }))} />
                    </div>
                    <div className="w-[20%]">
                      <label className={labelClass}>No Reg B3 {reqMark}</label>
                      <input required className={inputClass} placeholder="Contoh: 2026.02.108..." value={formData.no_reg_bahan} onChange={e => setFormData(p => ({ ...p, no_reg_bahan: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* 3. DETAIL B3 MULTIPLE */}
                <div className={sectionClass}>
                  <div className="border-b pb-2 mb-2 flex justify-between items-end">
                    <div>
                      <h3 className="font-bold text-[13px] flex items-center gap-2"><span className="w-1.5 h-4 bg-amber-500 rounded-full"></span> Detail B3</h3>
                      <p className="text-[10px] italic text-ink-500 mt-1">* Jika dalam satu SK terdapat beberapa B3, silahkan tekan tombol "Tambah B3" di kanan</p>
                    </div>
                    <Button size="sm" color="warning" variant="flat" onPress={handleAddB3} startContent={<PlusIcon className="size-3 stroke-2" />} className="h-7 text-[11px] font-bold text-amber-700 bg-amber-100 border border-amber-200">
                      Tambah B3
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.bahan_b3.map((b3, index) => (
                      /* LOGIKA HIGHLIGHT BARIS: ditambahkan transition dan state focus-within dari Tailwind di baris bawah ini */
                      <div key={index} className="flex gap-2 items-start bg-white p-2.5 rounded-lg border border-line shadow-sm relative transition-all duration-300 focus-within:bg-blue-50/60 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400">
                        <div className="w-[20%]">
                          <label className={labelClass}>CAS Number {reqMark}</label>
                          <Select
                            placeholder={loadingBahan ? "Loading..." : "Pilih/Ketik CAS..."}
                            isLoading={loadingBahan}
                            isDisabled={loadingBahan}
                            styles={reactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            options={bahanOptions}
                            value={bahanOptions.find(opt => opt.value === b3.cas_number) || null}
                            onChange={(selected) => handleB3Change(index, 'cas_number', selected ? selected.value : '')}
                          />
                        </div>
                        <div className="flex-1"><label className={labelClass}>Nama B3 {reqMark}</label><input required className={inputClass} value={b3.nama_bahan} disabled /></div>
                        <div className="flex-1"><label className={labelClass}>Nama Dagang {reqMark}</label><input required className={inputClass} value={b3.nama_dagang} onChange={e => handleB3Change(index, 'nama_dagang', e.target.value)} /></div>
                        <div className="w-[12%]"><label className={labelClass}>Kode HS (8 Digit) {reqMark}</label><input required maxLength={8} className={inputClass} value={b3.hs_code} onChange={e => handleB3Change(index, 'hs_code', e.target.value)} /></div>
                        <div className="w-[20%]">
                          <label className={labelClass}>Tujuan Penggunaan {reqMark}</label>
                          <select 
                            required
                            className={inputClass} 
                            value={b3.tujuan_penggunaan}
                            onChange={e => handleB3Change(index, 'tujuan_penggunaan', e.target.value)}
                          >
                            <option value="">-- Pilih --</option>
                            <option value="Bahan Baku">Bahan Baku</option>
                            <option value="Bahan Penolong">Bahan Penolong</option>
                            <option value="Laboratorium">Laboratorium</option>
                            <option value="Kegiatan Penunjang">Kegiatan Penunjang</option>
                          </select>
                        </div>
                        {index > 0 && (
                          <button type="button" onClick={() => handleRemoveB3(index)} className="absolute -right-2 -top-2 bg-red-100 text-red-600 hover:bg-red-500 hover:text-white p-1.5 rounded-full shadow-sm transition-colors border border-red-200">
                            <TrashIcon className="size-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4. GEOGRAFIS & UPLOAD SECTION */}
                <div className="flex gap-3 items-stretch">
                  
                  <div className="flex-1 grid grid-cols-2 gap-3 bg-surface-2 p-3.5 rounded-xl border border-line">
                    <div className="col-span-1">
                      <label className={labelClass}>Negara Asal {reqMark}</label>
                      <Select
                        placeholder="Pilih/Ketik Negara..."
                        isDisabled={formData.type_registrasi === 'produsen'}
                        styles={reactSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={negaraOptions}
                        value={negaraOptions.find(opt => opt.value === formData.asal_negara) || null}
                        onChange={(selected) => setFormData(prev => ({ 
                          ...prev, 
                          asal_negara: selected ? selected.value : '' 
                        }))}
                      />
                    </div>

                    <div className="col-span-1">
                      <label className={labelClass}>
                        Pelabuhan Bongkar {formData.type_registrasi !== 'produsen' && reqMark}
                      </label>
                      <Select
                        isMulti 
                        // ---> Logika Disable dan Placeholder Khusus Produsen <---
                        placeholder={formData.type_registrasi === 'produsen' ? "Tidak perlu diisi (Produsen)" : "Pilih..."} 
                        isDisabled={formData.type_registrasi === 'produsen' || loadingPelabuhanBongkar}
                        isLoading={loadingPelabuhanBongkar} 
                        styles={reactSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"        
                        options={pelabuhanOptions}  
                        value={pelabuhanValue}      
                        onChange={(selected) => setFormData(prev => ({ 
                          ...prev, 
                          pelabuhan_bongkar: selected ? selected.map(s => s.value) : [] 
                        }))}
                      />
                    </div>
                  </div>

                  <div className="w-[35%] bg-blue-50/50 p-3.5 rounded-xl border border-blue-200 shadow-sm flex flex-col justify-center">
                    <label className="text-xs font-bold text-blue-900 mb-1.5 block">
                      Unggah File SK (Pdf, Max 2Mb) {reqMark}
                    </label>
                    <input 
                      required
                      type="file" accept="application/pdf" 
                      className="text-[10.5px] w-full text-ink-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700 cursor-pointer border border-blue-200/80 rounded-lg p-[3px] bg-white h-[32px] overflow-hidden shadow-sm focus:ring-2 focus:ring-blue-500" 
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file && file.size > 2 * 1024 * 1024) { 
                          toast.error("Ukuran file melebihi 2MB!"); 
                          e.target.value = null; 
                          return; 
                        }
                        setFormData(p => ({ ...p, file_sk: file }));
                      }}
                    />
                  </div>
                </div>

              </div>

              {/* FOOTER */}
              <div className="px-5 py-3 border-t bg-surface-2 flex justify-end gap-3 rounded-b-2xl">
                <Button size="sm" variant="bordered" onPress={onClose}>Batal</Button>
                <Button size="sm" color="primary" type="submit" isLoading={submitting}>Simpan Data</Button>
              </div>

            </form>
          </div>
        </ModalContent>
      </Modal>

      {/* MODAL KONFIRMASI */}
      <Modal isDismissable={false} isOpen={showConfirm} onClose={() => setShowConfirm(false)} size="sm">
        <ModalContent className="p-5 text-center">
          <ExclamationCircleIcon className="size-10 mx-auto text-amber-500 mb-3" />
          <h3 className="font-bold mb-3">Pastikan data yang anda inputkan sudah benar.<br/>Apakah anda yakin ingin menambahkan Data SK tersebut ?</h3>
          <div className="flex gap-2">
            <Button className="flex-1" variant="flat" onPress={() => setShowConfirm(false)}>Batal</Button>
            <Button className="flex-1 bg-blue-600 text-white" onPress={handleExecuteSave} isLoading={submitting}>Ya, Simpan</Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}
